import { refreshToken } from 'api/authentication/refresh-token';
import { login as loginCall } from 'api/authentication/login';
import { GetDeviceVm } from 'api/management/get-device.vm';
import { GetWorkerVm } from 'api/management/get-worker.vm';
import { DeviceUUID } from 'device-uuid/lib/device-uuid';
import { getWorker } from 'api/management/get-worker';
import { getDevice } from 'api/management/get-device';
import { deleteAllCookies } from 'utils/cookies';
import { useSnackbar } from './use-snackbar';
import { useUserInfo } from './use-user-info';
import { useEffect, useState } from 'react';
import { instance as api } from 'api/core';
import { useCookies } from 'react-cookie';
import { AxiosError } from 'axios';

interface UseAuthContext {
  isAuthenticated: boolean;
  logout: () => void;
  login: (tenant: string, deviceId: string, username: string, password: string) => Promise<void>;
}

export const COOKIES_AUTH_ACCESS_KEY = 'access-key';
export const COOKIES_AUTH_REFRESH_TOKEN = 'refresh-token';
export const COOKIES_AUTH_USER = 'user';
export const COOKIES_AUTH_TENANT = 'tenant';

export const useAuth = (): UseAuthContext => {
  const [cookies, setCookies, removeCookies] = useCookies([COOKIES_AUTH_ACCESS_KEY, COOKIES_AUTH_REFRESH_TOKEN, COOKIES_AUTH_USER, COOKIES_AUTH_TENANT,]);
  const [refreshTokenInterceptor, setRefreshTokenInterceptor] = useState<number | null>(null);
  const [, setUserInfo] = useUserInfo();
  const snackbar = useSnackbar();

  const isAuthenticated = cookies[COOKIES_AUTH_ACCESS_KEY] && cookies[COOKIES_AUTH_USER] && cookies[COOKIES_AUTH_TENANT] ? true : false;

  const logout = () => {
    removeCookies(COOKIES_AUTH_ACCESS_KEY, { path: '/' });
    removeCookies(COOKIES_AUTH_REFRESH_TOKEN, { path: '/' });
    removeCookies(COOKIES_AUTH_USER, { path: '/' });
    removeCookies(COOKIES_AUTH_TENANT, { path: '/' });
    setUserInfo(null);
  };

  const login = async (tenant: string, deviceId: string, username: string, password: string) => {
    const res = await loginCall({ tenant, username, password, deviceId });
    setCookies(COOKIES_AUTH_ACCESS_KEY, res.data.accessToken, { path: '/' });
    setCookies(COOKIES_AUTH_REFRESH_TOKEN, res.data.refreshToken, { path: '/' });
    setCookies(COOKIES_AUTH_USER, res.data.userId, { path: '/' });
    setCookies(COOKIES_AUTH_TENANT, tenant, { path: '/' });
    loadUserInfo(res.data.userId);
  };

  const tokenRefreshInterceptor = (token: string) => async (err: AxiosError) => {
    if (!err.response || err.response.status !== 401) {
      return Promise.reject(err);
    }

    if (!token) {
      deleteAllCookies();
      return Promise.reject(err);
    }

    try {
      const refreshRes = await refreshToken(token);
      if (refreshRes.data?.token) {
        setCookies(COOKIES_AUTH_ACCESS_KEY, refreshRes.data.token, { path: '/' });
        err.config!!.headers['Authorization'] = refreshRes.data.token;
        return api.request(err.config!!);
      }
    } catch (err) {
      deleteAllCookies();
      return Promise.reject();
    }

    return Promise.reject(err);
  };

  const loadUserInfo = (userId: string) => {
    getDevice(new DeviceUUID().get(), {
      success: function (device: GetDeviceVm): void {
        getWorker(userId, {
          success: function (user: GetWorkerVm): void {
            setUserInfo({
              userId: userId,
              username: user.username,
              userName: user.name,
              warehouseId: device.warehouseId,
            });
          },
          error: function (err: Error) {
            snackbar.show("Não foi possivel obter informação de utilizador", null, "error")
          },
        });
      },
      error: function (err: Error) {
        snackbar.show("Não foi possivel obter informação do dispositivo", null, "error")
      },
    });
  };

  useEffect(() => {
    if (isAuthenticated) {
      if (!refreshTokenInterceptor) {
        const interceptorId = api.interceptors.response.use(
          (response) => response,
          tokenRefreshInterceptor(cookies[COOKIES_AUTH_REFRESH_TOKEN])
        );
        setRefreshTokenInterceptor(interceptorId);
      }
    } else {
      if (refreshTokenInterceptor) {
        api.interceptors.response.eject(refreshTokenInterceptor);
        setRefreshTokenInterceptor(null);
      }
    }

    return () => {
      if (refreshTokenInterceptor) {
        api.interceptors.response.eject(refreshTokenInterceptor);
        setRefreshTokenInterceptor(null);
      }
    };
  }, [cookies, refreshTokenInterceptor]);

  return { isAuthenticated, login, logout };
};
